import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Calculator, 
  Shield, 
  FileText, 
  CheckCircle,
  ArrowRight,
  Mail,
  Award,
  Search,
  Target,
  Building2
} from "lucide-react";

const FinancialCompliance = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: Calculator,
      title: "Financial Auditing",
      description: "Comprehensive financial audits and reviews to ensure accuracy, compliance, and transparency in financial reporting.",
      features: ["Financial Statement Audits", "Internal Audits", "Compliance Reviews", "Risk Assessment"]
    },
    {
      icon: Shield,
      title: "Regulatory Compliance",
      description: "Complete compliance management for various regulatory requirements including tax, labor, and industry-specific regulations.",
      features: ["Tax Compliance", "Labor Law Compliance", "Industry Regulations", "Regulatory Updates"]
    },
    {
      icon: FileText,
      title: "Registration & Certifications",
      description: "Assistance with business registrations, licenses, certifications, and other regulatory documentation requirements.",
      features: ["Business Registration", "License Applications", "Certification Support", "Documentation"]
    },
    {
      icon: Target,
      title: "Financial Planning",
      description: "Strategic financial planning and budgeting services to optimize financial performance and ensure sustainable growth.",
      features: ["Budget Planning", "Financial Forecasting", "Cost Management", "Investment Planning"]
    },
    {
      icon: Building2,
      title: "Statutory Compliance",
      description: "Comprehensive statutory compliance services including annual filings, returns, and regulatory submissions.",
      features: ["Annual Filings", "Tax Returns", "Regulatory Submissions", "Compliance Monitoring"]
    },
    {
      icon: Award,
      title: "Quality Assurance",
      description: "Quality assurance and control processes to ensure accuracy, reliability, and compliance in all financial operations.",
      features: ["Process Review", "Quality Control", "Accuracy Verification", "Best Practices"]
    }
  ];

  const stats = [
    { number: "50+", label: "Audits Completed", icon: Calculator },
    { number: "100%", label: "Compliance Rate", icon: Shield },
    { number: "20+", label: "Registrations", icon: FileText },
    { number: "8+", label: "Years Experience", icon: Award }
  ];

  const process = [
    {
      step: "01",
      title: "Assessment & Planning",
      description: "Comprehensive assessment of current financial status, compliance requirements, and development of action plans.",
      icon: Search
    },
    {
      step: "02",
      title: "Implementation",
      description: "Systematic implementation of compliance measures, financial controls, and regulatory requirements.",
      icon: Target
    },
    {
      step: "03",
      title: "Monitoring & Review",
      description: "Continuous monitoring of compliance status, financial performance, and regular reviews for improvement.",
      icon: Shield
    },
    {
      step: "04",
      title: "Reporting & Support",
      description: "Regular reporting on compliance status, financial performance, and ongoing support for continuous improvement.",
      icon: FileText
    }
  ];

  const benefits = [
    "Comprehensive compliance expertise",
    "Multi-sector regulatory knowledge",
    "Proactive compliance management",
    "Cost-effective solutions",
    "Timely and accurate reporting",
    "Ongoing support and guidance"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 text-white pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
        {/* Geometric Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-24 h-24 bg-white transform rotate-45"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-white transform rotate-45"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-white transform rotate-45"></div>
          <div className="absolute bottom-20 right-1/3 w-12 h-12 bg-white transform rotate-45"></div>
        </div>
        
        <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold border border-white/30">
                  <Calculator className="h-5 w-5 mr-3" />
                  Financial Excellence
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Complete <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-300">Financial Compliance</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  Comprehensive financial and statutory compliance solutions including audit reports, registrations, certifications, and regulatory support.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                 onClick={()=>navigate('/contactus')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-emerald-400 to-teal-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                  Get Compliance Support
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.button>
                <motion.button
                 onClick={()=>navigate('/services')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-green-900 transition-all duration-300"
                >
                  View Our Services
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white mb-6">Our Compliance Impact</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                        className="text-center"
                      >
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <stat.icon className="h-8 w-8 text-white" />
                        </div>
                        <div className="text-3xl font-bold text-white">{stat.number}</div>
                        <div className="text-white/80 text-sm">{stat.label}</div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Proven <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Compliance Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach that ensures complete compliance with all financial and regulatory requirements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 h-full border border-gray-200 hover:border-green-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center">
                        <step.icon className="h-8 w-8 text-white" />
                      </div>
                      <div className="text-4xl font-bold text-gray-200">{step.step}</div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-green-300 to-emerald-300 transform -translate-y-1/2"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Financial Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From auditing to compliance management, we provide end-to-end financial services that ensure regulatory compliance and financial health.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 h-full border border-gray-200 hover:border-green-300 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <div className="space-y-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <service.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Why Choose Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Financial Services?</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Partner with us for comprehensive financial compliance solutions that ensure regulatory adherence and financial stability.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-8 text-white"
            >
              <div className="space-y-6">
                <div className="text-center">
                  <Calculator className="h-16 w-16 mx-auto mb-4 text-white/80" />
                  <h3 className="text-2xl font-bold mb-2">Financial Excellence</h3>
                  <p className="text-white/90">Your compliance, our commitment</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <h4 className="text-lg font-semibold mb-4">Get Compliance Support</h4>
                  <p className="text-white/90 mb-6">
                    Ready to ensure complete financial compliance? Let's discuss how our services can help you meet all regulatory requirements.
                  </p>
                  <motion.button
                    onClick={() => navigate('/contactus')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-white text-green-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center justify-center"
                  >
                    Contact Us Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Ready to Ensure <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">Complete Compliance</span>?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-white/90">
              Let's discuss your financial compliance needs and create a comprehensive solution that ensures regulatory adherence and financial health.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
             
              <motion.button
               onClick={()=>navigate('/contactus')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-green-600 transition-all duration-300 flex items-center justify-center"
              >
                <Mail className="mr-2 h-5 w-5" />
                Discuss Your Needs
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FinancialCompliance;